import { useCallback, useEffect, useRef } from 'react'
import { useInfiniteQuery, useQuery } from 'react-query'

import '@hapana/shared/utils/type-defs.util'

import useSiteStore from '_stores/site.store'
import { getCheckIn, getCheckIns } from '_services/check-in/check-in.service'
import { useErrorHandler } from '@hapana/shared/web/hooks/errors.hooks'
import { GENERIC_ERROR_KEY } from '@hapana/shared/constants/errors.constants'

/** @type {function():InfiniteQueryResult} */
export const useCheckIns = ({ paginate }) => {
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  return useInfiniteQuery(
    [getCheckIns.key, { siteId, pageSize: paginate.pageSize }],
    getCheckIns.query,
    {
      getNextPageParam: (lastPage) => {
        if (lastPage?.checkIns?.pageInfo?.hasNextPage) {
          return {
            pageSize: paginate.pageSize,
            cursor: lastPage.checkIns.pageInfo.endCursor,
          }
        }
        return undefined
      },
      enabled: !!siteId,
      onError,
    }
  )
}

export const useCheckIn = (checkInId, options) => {
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  return useQuery([getCheckIn.key, { checkInId }], getCheckIn.query, {
    enabled: !!(checkInId && !(options?.isDisabled === true)),
    onError,
  })
}

/**
 * Listen for CHECK_IN_CREATED FCM events via BroadcastChannel from the service worker.
 * Replaces the old socket-based useNewCheckIn and useManualCheckInResponse hooks.
 */
export const useCheckInFCMEvent = (siteId, callback) => {
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    if (!siteId) return

    const channel = new BroadcastChannel('fcm-notifications')

    const handleMessage = (event) => {
      if (event.data?.type !== 'FCM_NOTIFICATION') return

      const fcmPayload = event.data.data
      const fcmData = fcmPayload?.data || fcmPayload
      if (fcmData?.type !== 'CHECK_IN_CREATED') return
      if (fcmData.siteId !== siteId) return

      callbackRef.current?.({
        checkInId: fcmData.checkInId,
        status: fcmData.status,
        hasOutstandingOrFailedPayments: fcmData.hasOutstandingOrFailedPayments === 'true',
      })
    }

    channel.addEventListener('message', handleMessage)

    return () => {
      channel.removeEventListener('message', handleMessage)
      channel.close()
    }
  }, [siteId])
}
